import { useEffect, useMemo, useState } from "react";
import { useDesign } from "../../lib/store";
import { formatLength } from "../../lib/geom";
import {
  editableWalls,
  hostsMatch,
  resolveHostSegment,
  wallLabel,
} from "../../lib/interiorWalls";
import { Button } from "../ui/Button";
import { Sheet } from "../ui/Sheet";
import {
  MOUNT_CATEGORIES,
  MOUNT_PRESETS,
  OPENING_CATEGORIES,
  openingPresetsFor,
  presetToMount,
  presetToOpening,
  type MountPreset,
  type OpeningPreset,
} from "../../lib/catalog";
import {
  ArrowLeftRight,
  DoorOpen,
  Plus,
  RulerDimensionLine,
  Trash2,
} from "lucide-react";
import { WallElevationCanvas } from "../canvas/WallElevationCanvas";
import { RoomCanvas } from "../canvas/RoomCanvas";
import { NumberStepper } from "../ui/NumberStepper";
import { Segmented } from "../ui/Segmented";
import { useIsMobile } from "../../lib/useViewportSize";
import { CategoryIcon } from "../icons/CategoryIcon";
import { MinimizableInspector } from "../ui/MinimizableInspector";
import {
  DesktopSidePanel,
  SelectionPanelHeader,
} from "../ui/ContextSidePanel";
import { ListingProvenance } from "../inspector/ListingProvenance";
import { Render3DButton } from "../render3d/Render3DButton";
import { WallFinishChip } from "../finishes/WallFinishChip";
import { interiorWallKey, outlineWallKey } from "../../lib/finishes/wallKeys";
import { roomSetting } from "../../lib/finishes/effective";
import type { Opening, Unit, WallMount } from "../../lib/types";

type PickerKind = "mount" | "opening";

export function WallView() {
  const room = useDesign((s) => s.design.room);
  const mounts = useDesign((s) => s.design.mounts);
  const openings = useDesign((s) => s.design.openings);
  const unit = useDesign((s) => s.design.unit);
  const wallIndex = useDesign((s) => s.activeWallIndex);
  const activeInterior = useDesign((s) => s.activeInteriorWall);
  const setActiveWallIndex = useDesign((s) => s.setActiveWallIndex);
  const setActiveInteriorWall = useDesign((s) => s.setActiveInteriorWall);
  const addMount = useDesign((s) => s.addMount);
  const updateMount = useDesign((s) => s.updateMount);
  const removeMount = useDesign((s) => s.removeMount);
  const addOpening = useDesign((s) => s.addOpening);
  const updateOpening = useDesign((s) => s.updateOpening);
  const removeOpening = useDesign((s) => s.removeOpening);
  const selectedMountId = useDesign((s) => s.selectedMountId);
  const selectedOpeningId = useDesign((s) => s.selectedOpeningId);

  const isMobile = useIsMobile();

  // Phones show one wall at a time with the plan below it; an empty
  // elevation ("Choose a wall") is a dead end there, so open on the wall
  // that already holds the most mounts (or the first wall).
  useEffect(() => {
    if (!isMobile) return;
    const s = useDesign.getState();
    if (s.activeWallIndex != null || s.activeInteriorWall) return;
    if (s.design.room.points.length < 3) return;
    const counts = new Map<number, number>();
    for (const m of s.design.mounts) {
      if (m.interiorWallId) continue;
      counts.set(m.wallIndex, (counts.get(m.wallIndex) ?? 0) + 1);
    }
    let best = 0;
    let bestCount = 0;
    for (const [i, n] of counts) {
      if (n > bestCount) {
        best = i;
        bestCount = n;
      }
    }
    s.setActiveWallIndex(best);
  }, [isMobile]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState<PickerKind>("mount");
  const [activeCat, setActiveCat] = useState(MOUNT_CATEGORIES[0].id);
  const [activeOpeningCat, setActiveOpeningCat] = useState(
    OPENING_CATEGORIES[0].id
  );

  const selected = useMemo(
    () => mounts.find((m) => m.id === selectedMountId) ?? null,
    [mounts, selectedMountId]
  );
  const selectedOpening = useMemo(
    () => openings.find((o) => o.id === selectedOpeningId) ?? null,
    [openings, selectedOpeningId]
  );

  const wallList = useMemo(() => editableWalls(room), [room]);
  const activeHost = activeInterior
    ? {
        wallIndex: 0,
        interiorWallId: activeInterior.wallId,
        interiorSegment: activeInterior.segment,
      }
    : wallIndex == null
      ? null
      : { wallIndex };
  const wall = useMemo(() => {
    if (!activeHost) return null;
    const seg = resolveHostSegment(room, activeHost);
    if (!seg) return null;
    return { a: seg.a, b: seg.b, length: seg.len };
  }, [activeHost, room]);
  const activeWallKey = activeInterior
    ? interiorWallKey(activeInterior.wallId, activeInterior.segment)
    : wallIndex != null
      ? outlineWallKey(wallIndex)
      : null;
  const activeLabel = activeInterior
    ? wallLabel(
        wallList.find(
          (w) => w.wallId === activeInterior.wallId && w.segment === activeInterior.segment
        )?.labelIndex ?? 0
      )
    : wallIndex != null
      ? wallLabel(wallIndex)
      : "—";

  function selectListedWall(entry: (typeof wallList)[number]) {
    if (entry.kind === "interior" && entry.wallId != null && entry.segment != null) {
      setActiveInteriorWall({ wallId: entry.wallId, segment: entry.segment });
    } else if (entry.boundaryIndex != null) {
      setActiveWallIndex(entry.boundaryIndex);
    }
    setPlanOpen(false);
  }

  function placeMount(p: MountPreset) {
    if (!wall || !activeHost) return;
    const placed = presetToMount(p, activeHost.wallIndex, wall.length / 2);
    addMount(
      activeInterior
        ? {
            ...placed,
            interiorWallId: activeInterior.wallId,
            interiorSegment: activeInterior.segment,
          }
        : placed
    );
    setPickerOpen(false);
  }

  function placeOpening(p: OpeningPreset) {
    if (!wall || !activeHost) return;
    const placed = presetToOpening(p, activeHost.wallIndex, wall.length / 2);
    addOpening(
      activeInterior
        ? {
            ...placed,
            interiorWallId: activeInterior.wallId,
            interiorSegment: activeInterior.segment,
          }
        : placed
    );
    setPickerOpen(false);
  }

  return (
    <div className="relative flex h-full">
      <main className="flex-1 relative bg-ink-50 min-w-0">
        {/* Elevation over floor plan; side by side on landscape phones so
            neither collapses on the short screen. */}
        <div className="absolute inset-x-0 bottom-[var(--canvas-bottom)] top-[var(--topbar-h)] flex flex-col land:flex-row gap-3 p-3 land:pr-[calc(0.75rem+var(--safe-right))] min-h-0">
          <section
            aria-label="Wall view"
            data-testid="wall-elevation"
            className="relative flex-[2] land:flex-[3] min-h-0 min-w-0 flex flex-col rounded-2xl bg-white ring-1 ring-ink-200 shadow-sm overflow-hidden"
          >
            <header className="shrink-0 flex items-center gap-2 border-b border-ink-100 px-3 py-1.5 min-w-0">
              <h2 className="sr-only md:not-sr-only text-sm font-semibold text-ink-900 shrink-0">Wall view</h2>
              <div className="hidden md:block h-4 w-px bg-ink-200 shrink-0" aria-hidden />
              <RulerDimensionLine className="size-4 text-ink-400 shrink-0" />
              {wall ? (
                <div className="min-w-0 truncate text-body-sm">
                  <span className="text-ink-500">Wall {activeLabel}</span>
                  <span className="mx-1.5 text-ink-300">·</span>
                  <span className="font-semibold text-ink-900 tabular-nums">
                    {formatLength(wall.length, unit)}
                  </span>
                </div>
              ) : (
                <div className="min-w-0 truncate text-body-sm font-semibold text-ink-900">
                  Choose a wall
                </div>
              )}
              {wall && activeWallKey && <WallFinishChip wallKey={activeWallKey} className="md:-my-1" />}
              <Render3DButton
                source="walls"
                variant="header"
                className="ml-auto md:-my-1"
                labelClassName="hidden md:inline"
                getFacing={() => {
                  const s = useDesign.getState();
                  return { wallIndex: s.activeWallIndex, interior: s.activeInteriorWall };
                }}
              />
              <button
                onClick={() => setPlanOpen(true)}
                className="md:hidden shrink-0 h-11 px-3 rounded-xl bg-ink-50 ring-1 ring-ink-200 text-body-sm font-medium inline-flex items-center gap-2 active:bg-ink-100 transition-colors"
              >
                <ArrowLeftRight className="size-4" />
                Switch wall
              </button>
            </header>
            <div className="relative flex flex-1 min-h-0">
              <div className="hidden md:flex shrink-0 flex-col gap-2 p-3 overflow-y-auto no-scrollbar pointer-events-none">
                {wallList.map((entry) => {
                  const len = entry.len;
                  const active = activeHost
                    ? hostsMatch(
                        {
                          wallIndex: entry.boundaryIndex ?? 0,
                          interiorWallId: entry.wallId,
                          interiorSegment: entry.segment,
                        },
                        activeHost
                      )
                    : false;
                  const count = mounts.filter((m) =>
                    hostsMatch(m, {
                      wallIndex: entry.boundaryIndex ?? 0,
                      interiorWallId: entry.wallId,
                      interiorSegment: entry.segment,
                    })
                  ).length;
                  const i = entry.labelIndex;
                  return (
                    <button
                      key={`${entry.kind}-${entry.wallId ?? entry.boundaryIndex}-${entry.segment ?? 0}`}
                      onClick={() => selectListedWall(entry)}
                      className={`pointer-events-auto inline-flex items-center gap-2 pl-2 pr-3 h-9 rounded-full text-xs font-medium ring-1 transition-all shadow-sm ${
                        active
                          ? "bg-ink-900 text-white ring-ink-900"
                          : "bg-white text-ink-700 ring-ink-200 hover:ring-ink-400"
                      }`}
                    >
                      <span
                        className={`size-6 inline-flex items-center justify-center rounded-full text-label font-semibold ${
                          active ? "bg-white/15 text-white" : "bg-ink-100 text-ink-700"
                        }`}
                      >
                        {wallLabel(i)}
                      </span>
                      <span>{formatLength(len, unit)}</span>
                      {count > 0 && (
                        <span
                          className={`ml-1 text-label px-1.5 rounded-full ${
                            active ? "bg-white/20" : "bg-accent-100 text-accent-700"
                          }`}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="relative flex-1 min-w-0">
                <WallElevationCanvas interactive />
              </div>
            </div>
          </section>

          <section
            aria-label="Floor plan"
            data-testid="floor-plan"
            className="relative flex-[3] land:flex-[2] min-h-0 min-w-0 rounded-2xl ring-1 ring-ink-200 overflow-hidden bg-ink-50"
          >
            <RoomCanvas interactive showWallLabels enableWallSelect hideInfoBadge={isMobile} />
          </section>
        </div>

        {/* FAB. The selection bar takes this row while something is selected. */}
        {wall && !selected && !selectedOpening && (
          <div className="md:hidden absolute bottom-[var(--float-bottom)] right-[calc(0.75rem+var(--safe-right))] z-20">
            <Button
              variant="primary"
              size="lg"
              onClick={() => setPickerOpen(true)}
              className="shadow-lg shadow-ink-900/20"
            >
              <Plus className="size-5" />
              Add to wall
            </Button>
          </div>
        )}

        {/* Mobile selection inspectors — desktop uses the right context panel. */}
        {isMobile && (
          <>
            <MinimizableInspector
              open={!!selected}
              onClose={() => useDesign.getState().selectMount(null)}
              itemId={selected?.id}
              title={selected?.name ?? ""}
              meta={
                selected
                  ? `${formatLength(selected.width, unit)} × ${formatLength(selected.height, unit)}`
                  : undefined
              }
              swatch={
                selected ? (
                  <div
                    className="size-5 rounded"
                    style={{ background: selected.color }}
                    aria-hidden
                  />
                ) : null
              }
            >
              {selected && wall && (
                <MountInspectorContent
                  selected={selected}
                  unit={unit}
                  wall={wall}
                  ceilingHeight={room.ceilingHeight}
                  onUpdate={(patch) => updateMount(selected.id, patch)}
                  onRemove={() => removeMount(selected.id)}
                />
              )}
            </MinimizableInspector>

            <MinimizableInspector
              open={!!selectedOpening}
              onClose={() => useDesign.getState().selectOpening(null)}
              itemId={selectedOpening?.id}
              title={selectedOpening?.name ?? ""}
              meta={
                selectedOpening
                  ? `${openingKindLabel(selectedOpening)} · ${formatLength(
                      selectedOpening.width,
                      unit
                    )} × ${formatLength(selectedOpening.height, unit)}`
                  : undefined
              }
              swatch={
                <div className="size-7 inline-flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <DoorOpen className="size-4" />
                </div>
              }
            >
              {selectedOpening && wall && (
                <OpeningInspectorContent
                  selected={selectedOpening}
                  wall={wall}
                  ceilingHeight={room.ceilingHeight}
                  onUpdate={(patch) => updateOpening(selectedOpening.id, patch)}
                  onRemove={() => removeOpening(selectedOpening.id)}
                />
              )}
            </MinimizableInspector>
          </>
        )}
      </main>

      {/* Desktop context panel — catalog when nothing selected, properties when selected */}
      <DesktopSidePanel>
        {selected ? (
          <>
            <SelectionPanelHeader
              title={selected.name}
              meta={`${formatLength(selected.width, unit)} × ${formatLength(selected.height, unit)}`}
              swatch={
                <div
                  className="size-7 rounded-lg"
                  style={{ background: selected.color }}
                  aria-hidden
                />
              }
              onClose={() => useDesign.getState().selectMount(null)}
            />
            <div className="flex-1 overflow-y-auto min-h-0">
              {wall && (
                <MountInspectorContent
                  selected={selected}
                  unit={unit}
                  wall={wall}
                  ceilingHeight={room.ceilingHeight}
                  onUpdate={(patch) => updateMount(selected.id, patch)}
                  onRemove={() => removeMount(selected.id)}
                />
              )}
            </div>
          </>
        ) : selectedOpening ? (
          <>
            <SelectionPanelHeader
              title={selectedOpening.name}
              meta={`${openingKindLabel(selectedOpening)} · ${formatLength(
                selectedOpening.width,
                unit
              )} × ${formatLength(selectedOpening.height, unit)}`}
              swatch={
                <div className="size-7 inline-flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <DoorOpen className="size-4" />
                </div>
              }
              onClose={() => useDesign.getState().selectOpening(null)}
            />
            <div className="flex-1 overflow-y-auto min-h-0">
              {wall && (
                <OpeningInspectorContent
                  selected={selectedOpening}
                  wall={wall}
                  ceilingHeight={room.ceilingHeight}
                  onUpdate={(patch) => updateOpening(selectedOpening.id, patch)}
                  onRemove={() => removeOpening(selectedOpening.id)}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="px-5 pt-[calc(var(--topbar-h)+1rem)] pb-3 border-b border-ink-100 shrink-0">
              <div className="text-caption uppercase tracking-wide text-ink-400 font-semibold">
                Step 3 of 3
              </div>
              <h2 className="text-xl font-bold text-ink-900 mt-1">Walls</h2>
              <p className="text-sm text-ink-500 mt-1">
                Pick a wall (left), then add doors, windows, or mount items. Drag to position; resize precisely.
              </p>
              <div className="mt-3">
                <Segmented
                  value={pickerKind}
                  onChange={(v) => setPickerKind(v)}
                  options={[
                    { id: "mount", label: "Mount" },
                    { id: "opening", label: "Doors & windows" },
                  ]}
                  size="sm"
                />
              </div>
            </div>
            {pickerKind === "mount" ? (
              <MountPicker
                activeCat={activeCat}
                setActiveCat={setActiveCat}
                onPick={placeMount}
                disabled={!wall}
              />
            ) : (
              <OpeningPicker
                activeCat={activeOpeningCat}
                setActiveCat={setActiveOpeningCat}
                onPick={placeOpening}
                disabled={!wall}
              />
            )}
          </>
        )}
      </DesktopSidePanel>

      <Sheet
        open={isMobile && pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={pickerKind === "mount" ? "Mount on wall" : "Add door or window"}
      >
        <div className="mb-3">
          <Segmented
            value={pickerKind}
            onChange={(v) => setPickerKind(v)}
            options={[
              { id: "mount", label: "Mount" },
              { id: "opening", label: "Doors & windows" },
            ]}
            size="sm"
          />
        </div>
        {pickerKind === "mount" ? (
          <MountPicker
            activeCat={activeCat}
            setActiveCat={setActiveCat}
            onPick={placeMount}
            disabled={!wall}
          />
        ) : (
          <OpeningPicker
            activeCat={activeOpeningCat}
            setActiveCat={setActiveOpeningCat}
            onPick={placeOpening}
            disabled={!wall}
          />
        )}
      </Sheet>

      <Sheet open={isMobile && planOpen} onClose={() => setPlanOpen(false)} title="Choose a wall">
        <div className="text-sm text-ink-500 mb-3">
          Tap any wall in the plan to view it head-on.
        </div>
        <div className="relative w-full h-[55vh] rounded-2xl overflow-hidden bg-ink-50">
          <RoomCanvas
            interactive={false}
            enableWallSelect
            showWallLabels
            hidePerson
            hideInfoBadge
          />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {wallList.map((entry) => {
            const active = activeHost
              ? hostsMatch(
                  {
                    wallIndex: entry.boundaryIndex ?? 0,
                    interiorWallId: entry.wallId,
                    interiorSegment: entry.segment,
                  },
                  activeHost
                )
              : false;
            return (
              <button
                key={`${entry.kind}-${entry.wallId ?? entry.boundaryIndex}-${entry.segment ?? 0}`}
                onClick={() => selectListedWall(entry)}
                className={`inline-flex items-center gap-2 px-3 h-11 rounded-xl text-sm font-medium ring-1 transition-all ${
                  active
                    ? "bg-ink-900 text-white ring-ink-900"
                    : "bg-white text-ink-700 ring-ink-200"
                }`}
              >
                <span
                  className={`size-6 inline-flex items-center justify-center rounded-full text-label font-semibold ${
                    active ? "bg-white/15 text-white" : "bg-ink-100 text-ink-700"
                  }`}
                >
                  {wallLabel(entry.labelIndex)}
                </span>
                Wall {wallLabel(entry.labelIndex)} · {formatLength(entry.len, unit)}
              </button>
            );
          })}
        </div>
      </Sheet>
    </div>
  );
}

function MountInspectorContent({
  selected,
  unit,
  wall,
  ceilingHeight,
  onUpdate,
  onRemove,
}: {
  selected: WallMount;
  unit: Unit;
  wall: { length: number };
  ceilingHeight: number;
  onUpdate: (patch: Partial<WallMount>) => void;
  onRemove: () => void;
}) {
  return (
    <>
      <ListingProvenance
        sourceUrl={selected.sourceUrl}
        sourcePrice={selected.sourcePrice}
        productId={selected.productId}
      />
      <div className="px-4 pb-3 flex flex-wrap gap-3">
        <NumberStepper
          label="Width"
          value={Math.round(selected.width)}
          onChange={(v) => onUpdate({ width: v })}
          min={2}
          max={Math.max(2, wall.length - 2)}
          step={1}
          suffix="in"
        />
        <NumberStepper
          label="Height"
          value={Math.round(selected.height)}
          onChange={(v) => onUpdate({ height: v })}
          min={2}
          max={Math.max(2, ceilingHeight - 2)}
          step={1}
          suffix="in"
        />
        <NumberStepper
          label="Center from floor"
          value={Math.round(selected.fromFloor)}
          onChange={(v) =>
            onUpdate({
              fromFloor: Math.min(ceilingHeight - 1, Math.max(1, v)),
            })
          }
          min={1}
          max={Math.max(1, ceilingHeight - 1)}
          step={1}
          suffix="in"
        />
        <NumberStepper
          label="From wall start"
          value={Math.round(selected.alongWall)}
          onChange={(v) =>
            onUpdate({
              alongWall: Math.min(wall.length - 1, Math.max(0, v)),
            })
          }
          min={0}
          max={Math.max(1, wall.length - 1)}
          step={1}
          suffix="in"
        />
      </div>
      <div className="border-t border-ink-100 px-3 py-2 flex items-center gap-1">
        <div className="text-caption text-ink-400 px-2">
          Top: {formatLength(selected.fromFloor + selected.height / 2, unit)} • Bottom:{" "}
          {formatLength(selected.fromFloor - selected.height / 2, unit)}
        </div>
        <div className="flex-1" />
        <Button variant="danger" size="sm" onClick={onRemove}>
          <Trash2 className="size-4" />
        </Button>
      </div>
    </>
  );
}

function OpeningInspectorContent({
  selected,
  wall,
  ceilingHeight,
  onUpdate,
  onRemove,
}: {
  selected: Opening;
  wall: { length: number };
  ceilingHeight: number;
  onUpdate: (patch: Partial<Opening>) => void;
  onRemove: () => void;
}) {
  return (
    <>
      <div className="px-4 pb-3 flex flex-wrap gap-3">
        <NumberStepper
          label="Width"
          value={Math.round(selected.width)}
          onChange={(v) => onUpdate({ width: v })}
          min={6}
          max={Math.max(6, wall.length - 2)}
          step={1}
          suffix="in"
        />
        <NumberStepper
          label="Height"
          value={Math.round(selected.height)}
          onChange={(v) => {
            const next: Partial<Opening> = { height: v };
            if (selected.kind === "door") next.fromFloor = v / 2;
            onUpdate(next);
          }}
          min={6}
          max={Math.max(6, ceilingHeight - 2)}
          step={1}
          suffix="in"
        />
        {selected.kind === "window" && (
          <NumberStepper
            label="Sill height"
            value={Math.round(selected.fromFloor - selected.height / 2)}
            onChange={(v) =>
              onUpdate({
                fromFloor: Math.max(
                  selected.height / 2,
                  v + selected.height / 2
                ),
              })
            }
            min={0}
            max={Math.max(0, ceilingHeight - selected.height)}
            step={1}
            suffix="in"
          />
        )}
        <NumberStepper
          label="From wall start"
          value={Math.round(selected.alongWall)}
          onChange={(v) =>
            onUpdate({
              alongWall: Math.min(wall.length - 1, Math.max(0, v)),
            })
          }
          min={0}
          max={Math.max(1, wall.length - 1)}
          step={1}
          suffix="in"
        />
      </div>
      <div className="border-t border-ink-100 px-3 py-2 flex items-center gap-1">
        <div className="flex-1" />
        <Button variant="danger" size="sm" onClick={onRemove}>
          <Trash2 className="size-4" />
        </Button>
      </div>
    </>
  );
}

function openingKindLabel(o: Pick<Opening, "kind" | "style">): string {
  if (o.style === "cutout") return "Cutout";
  if (o.style === "gate") return "Gate";
  return o.kind === "door" ? "Door" : "Window";
}

function OpeningPicker({
  activeCat,
  setActiveCat,
  onPick,
  disabled,
}: {
  activeCat: OpeningPreset["kind"];
  setActiveCat: React.Dispatch<React.SetStateAction<OpeningPreset["kind"]>>;
  onPick: (p: OpeningPreset) => void;
  disabled?: boolean;
}) {
  const setting = useDesign((s) => roomSetting(s.design.room.finishes));
  const items = openingPresetsFor(setting).filter((p) => p.kind === activeCat);
  const unit = useDesign((s) => s.design.unit);
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-3 pt-3 pb-2 border-b border-ink-100 sticky top-0 bg-white">
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          {OPENING_CATEGORIES.map((c) => {
            const active = c.id === activeCat;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`shrink-0 h-9 px-3 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? "bg-ink-900 text-white"
                    : "bg-ink-50 text-ink-600 hover:bg-ink-100"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 grid grid-cols-2 gap-2 auto-rows-min">
        {items.map((p) => (
          <button
            key={p.name}
            disabled={disabled}
            onClick={() => onPick(p)}
            className="group flex flex-col items-stretch gap-2 p-3 rounded-2xl bg-ink-50 ring-1 ring-ink-100 hover:ring-ink-300 hover:bg-white transition-all text-left disabled:opacity-50 disabled:pointer-events-none"
          >
            <div className="h-16 w-full rounded-lg flex items-center justify-center bg-white ring-1 ring-ink-100">
              {p.kind === "door" && p.style === "cutout" ? (
                <div className="h-10 w-8 rounded-sm bg-white ring-1 ring-ink-400" />
              ) : p.kind === "door" && p.style === "gate" ? (
                <div className="flex h-9 w-10 gap-0.5 rounded-sm bg-amber-50 p-0.5 ring-1 ring-amber-700/40" aria-hidden>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex-1 rounded-[1px] bg-amber-300" />
                  ))}
                </div>
              ) : p.kind === "door" ? (
                <div className="h-10 w-8 rounded-sm bg-amber-50 ring-1 ring-amber-300" />
              ) : (
                <div className="h-10 w-12 rounded-sm bg-sky-50 ring-1 ring-sky-300 grid grid-cols-2 grid-rows-2 gap-px p-px">
                  <div className="bg-sky-100" />
                  <div className="bg-sky-100" />
                  <div className="bg-sky-100" />
                  <div className="bg-sky-100" />
                </div>
              )}
            </div>
            <div className="text-body-sm font-semibold text-ink-900 leading-tight">
              {p.name}
            </div>
            <div className="text-caption text-ink-400">
              {formatLength(p.width, unit)} × {formatLength(p.height, unit)}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function MountPicker({
  activeCat,
  setActiveCat,
  onPick,
  disabled,
}: {
  activeCat: string;
  setActiveCat: React.Dispatch<React.SetStateAction<MountPreset["category"]>>;
  onPick: (p: MountPreset) => void;
  disabled?: boolean;
}) {
  const items = MOUNT_PRESETS.filter((p) => p.category === activeCat);
  const unit = useDesign((s) => s.design.unit);
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-3 pt-3 pb-2 border-b border-ink-100 sticky top-0 bg-white">
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          {MOUNT_CATEGORIES.map((c) => {
            const active = c.id === activeCat;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`shrink-0 h-9 px-3 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? "bg-ink-900 text-white"
                    : "bg-ink-50 text-ink-600 hover:bg-ink-100"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 grid grid-cols-2 gap-2 auto-rows-min">
        {items.map((p) => (
          <button
            key={p.name}
            disabled={disabled}
            onClick={() => onPick(p)}
            className="group flex flex-col items-stretch gap-2 p-3 rounded-2xl bg-ink-50 ring-1 ring-ink-100 hover:ring-ink-300 hover:bg-white transition-all text-left disabled:opacity-50 disabled:pointer-events-none"
          >
            <div
              className="h-16 w-full rounded-lg flex items-center justify-center"
              style={{ background: p.color + "22" }}
            >
              {p.category === "tv" ? (
                <div className="h-10 w-14 rounded bg-ink-900 ring-2 ring-ink-700" />
              ) : (
                <CategoryIcon name="square" className="size-8 text-ink-700" color={p.color} />
              )}
            </div>
            <div className="text-body-sm font-semibold text-ink-900 leading-tight">
              {p.name}
            </div>
            <div className="text-caption text-ink-400">
              {formatLength(p.width, unit)} × {formatLength(p.height, unit)}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
